import { memo, useCallback, use, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router'
import { useIsFetching } from '@tanstack/react-query'
import { Input } from '@/components/ui'
import { CogsAllowedTypes, CogsSelectableKeys, DirectCostUnitCost, DirectCostUnitCostPostBody } from '../api/types'
import { usePageContext } from '@/lib/context/page'
import { useFormatLocalizedNumber } from '@/lib/hooks/useLocaleFormatter'
import { cn, parseInputValue } from '@/lib/utils'
import { usePostDirectCostsPeriodicData, useSwitchCogsColumnType } from '../api'
import {
  ShowInverseValue,
  RowWithInputTypeContext,
  InputCellPopoverButton,
  InputTypeContainer,
  useOrderTypeManager
} from '@/components/shared/tables'
import { isNullOrUndefinedOrEmpty } from '@/lib/utils/parsers'
import { useFinancialModelSettingsStore } from '@/feature/financial-model/settings/store/financialModelSettingsStore'
import { PeriodTableInput } from '@/components/shared/tables/table-cell'

type InputCellProps = {
  period: DirectCostUnitCost
  selectedKey: CogsSelectableKeys
  categoryId?: string
}

const useCogsInputCell = ({ period, selectedKey, categoryId }: InputCellProps) => {
  const { state } = usePageContext()
  const { rowType } = use(RowWithInputTypeContext) ?? {
    rowType: `absolute`
  }
  const { id: financialModelId } = useParams<{ id: string }>()

  const [customType, setCustomType] = useOrderTypeManager({
    row: period,
    selectedKey,
    rowType
  })

  const financialModelSettings = useFinancialModelSettingsStore(settings => settings.settings[financialModelId!])
  const formatNumber = useFormatLocalizedNumber()

  const formatValue = useCallback(
    (val: string | number | null) => {
      if (val === null || val === ``) return ``
      let periodType = customType
      if (state.queryPeriod === `all` && period.period_type !== state.period) {
        periodType = period.config[selectedKey]?.type ?? customType
      }
      if (periodType === `absolute`) {
        if (selectedKey === `percentage_of_revs`) {
          return `${formatNumber(val, {
            style: `percent`
          })}`
        }
        return `${formatNumber(val, {
          style: `currency`,
          currency: financialModelSettings?.currency?.functionalCurrency
        })}`
      }
      return `${formatNumber(val, { style: `percent` })}`
    },
    [
      customType,
      financialModelSettings?.currency?.functionalCurrency,
      formatNumber,
      period.config,
      period.period_type,
      selectedKey,
      state.period,
      state.queryPeriod
    ]
  )

  const [value, setValue] = useState<string | null>(period[selectedKey]?.toString() ?? null)
  const queries = useIsFetching({
    queryKey: [`direct-costs`]
  })

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const parsedValue = parseInputValue(e.target.value)
      setValue(parsedValue)
    },
    [setValue]
  )

  const handleFocus = useCallback(() => {
    setValue(period[selectedKey]?.toFixed(2) ?? ``)
  }, [period, selectedKey])

  const { mutate: updateDirectCosts, isPending: isUpdating } = usePostDirectCostsPeriodicData(financialModelId!)

  const handleUpdate = useCallback(() => {
    const objectToUpdate = {
      ...period,
      ...(categoryId ? { category_id: categoryId } : {}),
      config: {
        ...period.config,
        [selectedKey]: {
          ...period.config[selectedKey],
          type: customType
        }
      },
      [selectedKey]: Number(value)
    } satisfies DirectCostUnitCostPostBody
    updateDirectCosts(objectToUpdate)
  }, [period, categoryId, selectedKey, customType, value, updateDirectCosts])

  useEffect(() => {
    if (queries === 0) {
      if (!isNullOrUndefinedOrEmpty(period[selectedKey])) {
        setValue(formatValue(period[selectedKey]))
      }
    }
  }, [period, selectedKey, formatValue, queries])

  const handleBlur = useCallback(() => {
    if (!value && !period[selectedKey]) {
      return
    }
    if (formatValue(value) === formatValue(period[selectedKey])) {
      return setValue(formatValue(period[selectedKey]))
    }

    handleUpdate()
  }, [formatValue, period, selectedKey, value, handleUpdate])

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === `Enter`) {
      e.currentTarget.blur()
    }
  }, [])

  const formatInverseValue = useCallback(
    (val: number | string | null) => {
      if (val === null) return ``
      if (selectedKey === `percentage_of_revs`) {
        return `${formatNumber(val, {
          style: `percent`
        })}`
      }
      return `${formatNumber(val, {
        style: `currency`,
        currency: financialModelSettings?.currency?.functionalCurrency
      })}`
    },
    [financialModelSettings?.currency?.functionalCurrency, formatNumber, selectedKey]
  )

  const inverseValue = useMemo(() => {
    if (period.inverse_values[selectedKey]) {
      return formatInverseValue(period.inverse_values[selectedKey])
    }
    return null
  }, [formatInverseValue, period.inverse_values, selectedKey])

  const { mutate: switchCogsColumnType, isPending: isSwitchingColumnType } = useSwitchCogsColumnType(financialModelId!)

  const handleSwitchColumnType = useCallback(
    (newType: CogsAllowedTypes) => {
      setCustomType(newType)
      switchCogsColumnType({
        column_name: selectedKey,
        col_type: newType,
        period_type: period.period_type,
        month: period.fiscal_month,
        year: period.fiscal_year,
        product_id: period.product_id
      })
    },
    [setCustomType, switchCogsColumnType, selectedKey, period.period_type, period.fiscal_month, period.fiscal_year, period.product_id]
  )

  return {
    value,
    onChange: handleChange,
    onFocus: handleFocus,
    onKeyDown: handleKeyDown,
    onBlur: handleBlur,
    isFetching: queries > 0 || isUpdating || isSwitchingColumnType,
    inverseValue,
    customType,
    rowType,
    handleSwitchColumnType
  }
}

type InputCellClassNames = Pick<React.ComponentProps<typeof Input>, `className`>

export const CogsInputCell = memo(function CogsInputCell({
  period,
  selectedKey,
  readonly,
  className,
  categoryId
}: InputCellProps & {
  readonly?: true
  className?: InputCellClassNames[`className`]
}) {
  const { state } = usePageContext()
  const { isFetching, inverseValue, value, customType, rowType, handleSwitchColumnType, ...fieldProps } = useCogsInputCell({
    period,
    selectedKey,
    categoryId
  })

  if (readonly || state.view === `yearly` || period.period_type !== state.period) {
    return <span>{value || `--`}</span>
  }
  return (
    <InputTypeContainer
      className={cn(`flex w-full items-center`, {
        [`pointer-events-none opacity-50`]: isFetching,
        [`pr-2`]: rowType === `mix` && customType !== `absolute`
      })}
    >
      {rowType === `mix` && (
        <InputCellPopoverButton
          type={customType}
          handleSetType={type => handleSwitchColumnType(type as CogsAllowedTypes)}
          allowedTypes={period.config[selectedKey]?.allowed_types ?? [`absolute`]}
        />
      )}
      <PeriodTableInput {...fieldProps} value={value ?? ``} className={cn(className)} placeholder='insert value' />
      <ShowInverseValue when={period[selectedKey] !== null && customType !== `absolute`}>
        <span className='flex-shrink-0 text-xs text-blue-400'>({inverseValue})</span>
      </ShowInverseValue>
    </InputTypeContainer>
  )
})
